import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Milk, Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Milk className="h-5 w-5" />
          </div>
          <span className="font-heading text-lg font-bold">MilkERP</span>
        </div>
        {sent ? (
          <div className="space-y-4 text-center" data-testid="forgot-success">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <MailCheck className="h-6 w-6" />
            </div>
            <h2 className="font-heading text-2xl font-bold">{t("forgot.checkTitle")}</h2>
            <p className="text-sm text-muted-foreground">
              If an account exists for <b>{email}</b>, we've sent a password reset link. It expires in 1 hour.
            </p>
            <Link to="/login"><Button variant="outline" className="w-full">{t("forgot.back")}</Button></Link>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-bold tracking-tight">{t("forgot.title")}</h2>
            <p className="mb-6 mt-1 text-sm text-muted-foreground">{t("forgot.subtitle")}</p>
            {error && <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">{error}</div>}
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@center.com" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("forgot.send")}
              </Button>
            </form>
            <Link to="/login" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /> {t("forgot.back")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
